import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import type { MasterControlWorkflowEntry } from "@/hooks/useMasterControl";

type MCWorkflowInspectorDrawerProps = {
  entry: MasterControlWorkflowEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReset: () => Promise<void>;
  resetting: boolean;
  canReset: boolean;
};

export function MCWorkflowInspectorDrawer({
  entry,
  open,
  onOpenChange,
  onReset,
  resetting,
  canReset,
}: MCWorkflowInspectorDrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-primary-foreground dark:text-amber-200">
                Workflow detail
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                Inspect full LangGraph payload and reset the runtime if needed.
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="rounded-full border border-border p-1 text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
              onClick={() => onOpenChange(false)}
            >
              ×
            </button>
          </div>

          {entry ? (
            <div className="mt-4 space-y-4 text-sm text-foreground/80">
              <div className="rounded-xl border border-border bg-background/30 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Metadata</p>
                <dl className="mt-2 grid gap-4 md:grid-cols-2">
                  <div>
                    <dt className="text-xs text-muted-foreground">Workflow</dt>
                    <dd className="font-semibold text-foreground">{entry.workflow_id}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Agent</dt>
                    <dd>{entry.agent}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Status</dt>
                    <dd className="capitalize">{entry.status}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Last event</dt>
                    <dd>{entry.last_event_at ? new Date(entry.last_event_at).toLocaleString() : "—"}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Raw payload</p>
                <CodeBlock
                  language="json"
                  value={JSON.stringify(entry.raw ?? {}, null, 2)}
                  className="max-h-[320px]"
                />
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Select a workflow to view details.</p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button
              variant="destructive"
              disabled={resetting || !canReset}
              onClick={() => {
                void onReset();
              }}
              title={canReset ? undefined : "Upgrade to unlock reset access"}
            >
              {resetting ? "Resetting…" : "Reset workflow engine"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default MCWorkflowInspectorDrawer;
